import Vue from 'vue';
import trace from "../trace.mjs";
import { pushEvent } from "../analytics.mjs";

/**
 * @typedef {Vue} DialogContainer
 * @property {function} pushDialog
 * @property {function} removeDialog
 * @property {function} onDialogEmitter
 * @property {function} updateState
 */

const containerElement = document.getElementById('dialog-container');

const dialogContainerDefinition = {
    el   : '#dialog-container',
    data : {
        dialogs : [],
        overlay : false
    },
    computed : {
        canCloseOverlay : function() {
            return (
                this.dialogs.length &&
                this.dialogs.slice(-1)[0]
                    .canCloseOverlay()
            );
        }
    },
    methods : {
        /**
         * Push a DialogBox onto the stack
         * @param {DialogBox} dialog
         */
        pushDialog : function( dialog )
        {
            trace.info( 'Dialog manager: new dialog pushed onto stack', dialog );
            this.dialogs.push( dialog );
            this.updateState();
        },
        /**
         * Remove a DialogBox from the stack
         * @param {DialogBox} target
         * @param {string}    result
         */
        removeDialog : function( target, result=null )
        {
            trace.info( 'Dialog manager: requesting dialog removal', target );

            if( target.onBeforeClose() !== false )
            {
                this.dialogs = this.dialogs.filter( dialog => dialog !== target );
                target.onClose();

                if( result ) {
                    pushEvent( 'dialog result', target.constructor.name, result );
                }

                trace.info( 'Dialog manager: dialog removed', target );
                this.updateState();

                return true;
            }

            trace.warn( 'Dialog manager: denied close request', target );
            return false;
        },
        /**
         * Request closing of all dialogs that can be closed via overlay
         */
        closeFromOverlay : function()
        {
            trace.info('Dialog manager: requesting close via overlay');
            let dialogsInReverse = [ ...this.dialogs ].reverse();

            // This could be broken out of the loop if needed, but for now we'll add 'the thing'
            // so that a click only closes the top layer dialog. I haven't quite decided on functionality
            // as yet, so I don't want to remove the full structure.
            for( const dialog of dialogsInReverse ) {
                if( ! dialog.canCloseOverlay() ) {
                    trace.warn('Dialog manager: close blocked by dialog', dialog );
                    break;
                }

                this.removeDialog( dialog, 'dismissed: overlay' );
                break; // this is "the thing" that only closes the top layer.
            }
            this.updateState();
        },
        /**
         * Emit a value or set of values through a DialogBox
         * @param {DialogBox} dialog
         * @param {any} values
         */
        onDialogEmitter : function( dialog, ...values )
        {
            dialog.onValueEmitted( ...values );
        },
        /**
         * Update the state of the component
         * Called internally after any addition/removal of dialog boxes
         */
        updateState : function()
        {
            this.overlay = ( this.dialogs.length > 0 );

            document
                .getElementsByTagName('html')[0]
                .classList
                .toggle( 'overlay-active', this.overlay )
            ;
        }
    }
};

/**
 * Dialog Container Vue Component
 * @type {DialogContainer|null}
 */
const dialogContainer = containerElement
    ? new Vue(dialogContainerDefinition)
    : null ;

/**
 * Manager object for dialog container
 *
 * This class implements singleton and facade pattern to provide an external interface
 * with which to interact with the dialog container
 *
 * @property {DialogContainer} container
 */
class DialogBoxManager {

    /**
     * Dialog Box Manager constructor
     * @param {DialogContainer} container
     */
    constructor( container ) {
        this.container = container;
    }

    /**
     * Push a dialog box onto the stack
     * @param {DialogBox} dialog
     */
    pushDialog( dialog ) {
        dialog.setManager(this);
        this.container.pushDialog(dialog);
    };

    /**
     * Remove a dialog box from the stack
     * @param {DialogBox} dialog
     * @param {string}    result
     */
    removeDialog( dialog, result=null ) {
        this.container.removeDialog( dialog, result );
    }
}

export const dialogBoxManager = new DialogBoxManager(dialogContainer);


